import {
  IconButton,
  Heading,
  Text,
  Box,
  ChakraModal,
  ChakraModalBody,
  ChakraModalContent,
  ChakraModalFooter,
  ChakraModalOverlay,
  ChakraAspectRatio,
  CloseIcon,
  ButtonGroup,
} from "@hightouch/htui";
import { HTImage } from "src/components/image";
import { Column } from "@hightouch/htui";

// Video needs both sources so every browser has a playable format (WebM, MP4 fallback).
// `loop` defaults to true; set false for one-shot animations that should hold
// on their final frame instead of restarting.
type NewFeatureDialogMedia =
  | { type: "image"; src: string }
  | { type: "video"; webm: string; mp4: string; loop?: boolean };

const NewFeatureDialogRoot = ({
  onClose,
  media,
  mediaPosition = "center",
  mediaAspectRatio = 598 / 188,
  size = "xl",
  children,
}: {
  onClose: () => void;
  media: NewFeatureDialogMedia;
  mediaPosition?: string;
  mediaAspectRatio?: number;
  size?: React.ComponentProps<typeof ChakraModal>["size"];
  children: React.ReactNode;
}) => {
  return (
    <ChakraModal
      isOpen
      isCentered
      size={size}
      motionPreset="none"
      closeOnOverlayClick={false}
      onClose={onClose}
    >
      <ChakraModalOverlay />
      <ChakraModalContent
        borderRadius="2xl"
        boxShadow="lg"
        overflow="hidden"
        padding={0}
        pos="relative"
        w="600px"
      >
        <Column width="100%" position="relative">
          <ChakraAspectRatio ratio={mediaAspectRatio} pos="relative" w="100%">
            {media.type === "video" ? (
              <Box
                as="video"
                autoPlay
                loop={media.loop ?? true}
                muted
                playsInline
                aria-hidden
                objectFit="cover"
                objectPosition={mediaPosition}
              >
                <source src={media.webm} type="video/webm" />
                <source src={media.mp4} type="video/mp4" />
              </Box>
            ) : (
              <HTImage
                decorative
                src={media.src}
                style={{ objectFit: "cover", objectPosition: mediaPosition }}
              />
            )}
          </ChakraAspectRatio>
          <Box
            pos="absolute"
            width="100%"
            height={8}
            left={0}
            right={0}
            bottom={0}
            bg="linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))"
          />
        </Column>

        <IconButton
          aria-label="Close modal"
          icon={CloseIcon}
          pos="absolute"
          top={4}
          right={4}
          onClick={onClose}
        />

        {children}
      </ChakraModalContent>
    </ChakraModal>
  );
};

const DialogHeading = ({ children }: { children: React.ReactNode }) => {
  return <Heading size="xl">{children}</Heading>;
};

const Body = ({ children }: { children: React.ReactNode }) => {
  return (
    <ChakraModalBody
      display="flex"
      flexDir="column"
      gap={4}
      m={0}
      pt={6}
      px={6}
      pos="relative"
    >
      {children}
    </ChakraModalBody>
  );
};

const Actions = ({ children }: { children: React.ReactNode }) => {
  return (
    <ChakraModalFooter mt={0} p={6}>
      <ButtonGroup>{children}</ButtonGroup>
    </ChakraModalFooter>
  );
};

const ListItem = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box as="li">
      <Text as="span" color="text.secondary">
        {children}
      </Text>
    </Box>
  );
};

export const NewFeatureDialog = Object.assign(NewFeatureDialogRoot, {
  Heading: DialogHeading,
  Body,
  Actions,
  ListItem,
});
